import type {ReactElement} from 'react';
import {useRef} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import {getWriteBucket} from '@Utils/s3.util';
import styles from './font-button.module.scss';

interface FontButtonProps {
  selectedFontFamily: string;
  className?: string;
  isFontSuppported?: boolean;
  /**
   * When true the target items have different fonts, so a placeholder is shown instead of one
   * item's font preview.
   */
  isMixed?: boolean;
  'data-testid'?: string;
  onClick(): void;
}

const query: ContainerQueryObject = {
  small: {
    minWidth: 0,
    maxWidth: 161,
  },
  large: {
    minWidth: 163,
  },
};

export function FontButton({onClick = (): void => {}, isFontSuppported = true, isMixed = false, ...props}: FontButtonProps): ReactElement<any> {
  const containerRef = useRef<HTMLDivElement>(null);
  const params = useContainerQuery(containerRef, query);

  const getShortFontImage = (): string => {
    return `${window.PMW.util.repoURL(`fonts/${props.selectedFontFamily}_short.png?`, getWriteBucket())}`;
  };

  const getNormalFontImage = (): string => {
    return `${window.PMW.util.repoURL(`fonts/${props.selectedFontFamily}.png?`, getWriteBucket())}`;
  };

  const getContainerClasses = (): string => {
    if (isMixed) {
      return 'flex-center';
    }
    return !isFontSuppported ? `flex-h-row flex-items-center ${styles.notSupported}` : 'flex-center';
  };

  const getContent = (): ReactElement => {
    if (isMixed) {
      return (
        <div className={styles.mixedPlaceholder}>
          <div className={`${styles.mixedPlaceholderDash} corner-radius-full`} />
          <div className={`${styles.mixedPlaceholderDash} corner-radius-full`} />
        </div>
      );
    }
    return (
      <>
        {isFontSuppported ? null : <Icon icon="icon-exclamation-triangle" size={IconSize.SIZE_ICON_20} className="content-danger spacing-m-l-2 spacing-m-r-2" />}
        <img
          loading="lazy"
          className={`${styles.fontImage} ${!isFontSuppported ? styles.notSupportedImg : 'spacing-p-l-3'} spacing-p-t-2 spacing-p-b-2 spacing-p-r-3`}
          src={`${params.large ? getShortFontImage() : getNormalFontImage()}`}
          alt=""
        />
      </>
    );
  };

  return (
    <div onClick={onClick} ref={containerRef} data-testid={props['data-testid']} className={`${styles.fontImageContainer}  radius-4 hover-transition-all ${getContainerClasses()}`}>
      {getContent()}
    </div>
  );
}
